import { inject } from '@angular/core';

import { Actions, ROOT_EFFECTS_INIT, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, catchError, forkJoin, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import {
  GenericServiceValue,
  PersonalDoctorValue,
  PharmacologicalTreatmentValue,
  PrescriptionFormValue,
} from '../models/prescription.interface';

import { PrescriptionsDraftsStorageService } from '../services/prescriptions-drafts-storage.service';
import { PrescriptionsService } from '../services/prescriptions.service';
import {
  addPrescriptionDraft,
  clearPrescriptionDrafts,
  deletePrescriptionDraft,
  loadPrescriptionDraftsFromStorage,
  updatePrescriptionDraft,
} from './prescriptions-drafts.actions';
import {
  clearPrescriptions,
  processPrescriptionsDrafts,
  processPrescriptionsDraftsFailure,
  processPrescriptionsDraftsSuccess,
  refreshPrescriptionsData,
  resetPrescriptionsState,
} from './prescriptions.actions';
import { selectPrescriptionDrafts } from './prescriptions.selectors';

export const loadPrescriptionDraftsFromStorageEffect = createEffect(
  (actions$ = inject(Actions), storageService = inject(PrescriptionsDraftsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      map(() => loadPrescriptionDraftsFromStorage({ drafts: storageService.loadDrafts() }))
    ),
  { functional: true }
);

export const addPrescriptionDraftEffect = createEffect(
  (actions$ = inject(Actions), storageService = inject(PrescriptionsDraftsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(addPrescriptionDraft),
      tap((action: { draft: PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue> } & Action) => {
        const currentDrafts = storageService.loadDrafts();
        storageService.saveDrafts([...currentDrafts, action.draft]);
      })
    ),
  { functional: true, dispatch: false }
);

export const updatePrescriptionDraftEffect = createEffect(
  (actions$ = inject(Actions), storageService = inject(PrescriptionsDraftsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(updatePrescriptionDraft),
      tap((action: { draft: PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue> } & Action) => {
        const currentDrafts = storageService.loadDrafts();
        const existingIndex = currentDrafts.findIndex(d => d.id === action.draft.id);
        const updatedDrafts = existingIndex !== -1
          ? currentDrafts.map(d => (d.id === action.draft.id ? { ...d, ...action.draft } : d))
          : [...currentDrafts, action.draft];
        storageService.saveDrafts(updatedDrafts);
      })
    ),
  { functional: true, dispatch: false }
);

export const deletePrescriptionDraftEffect = createEffect(
  (actions$ = inject(Actions), storageService = inject(PrescriptionsDraftsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(deletePrescriptionDraft),
      tap((action: { id: string } & Action) => {
        const updatedDrafts = storageService.loadDrafts().filter(d => d.id !== action.id);
        storageService.saveDrafts(updatedDrafts);
      })
    ),
  { functional: true, dispatch: false }
);

export const clearPrescriptionDraftsEffect = createEffect(
  (actions$ = inject(Actions), storageService = inject(PrescriptionsDraftsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(clearPrescriptionDrafts),
      tap(() => storageService.clearDrafts())
    ),
  { functional: true, dispatch: false }
);

export const processPrescriptionsDraftsEffect = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    prescriptionsService = inject(PrescriptionsService),
    storageService = inject(PrescriptionsDraftsStorageService)
  ): Observable<Action> =>
    actions$.pipe(
      ofType(processPrescriptionsDrafts),
      withLatestFrom(store.select(selectPrescriptionDrafts)),
      switchMap(([action, drafts]) => {
        const draftsRequestBody = drafts
          .map(formValue => {
            if ('code' in formValue.metadata) {
              return prescriptionsService.createServiceProcessBody(<PrescriptionFormValue<GenericServiceValue>>formValue);
            }
            if ('doctorId' in formValue.metadata) {
              return prescriptionsService.createPersonalDoctorProcessBody(<PrescriptionFormValue<PersonalDoctorValue>>formValue);
            }
            return prescriptionsService.createMedicationProcessBody(<PrescriptionFormValue<PharmacologicalTreatmentValue>>formValue);
          })
          .filter(draft => !!draft.guaranteeId);
        const newDraftsRequestBody = draftsRequestBody.filter(draft => draft?.originalPrescriptionId === null);
        const updatedDraftsRequestBody = draftsRequestBody.filter(draft => !!draft?.originalPrescriptionId);
        if (newDraftsRequestBody?.length === 0 && updatedDraftsRequestBody?.length === 0) {
          return of(processPrescriptionsDraftsFailure({ error: 'Drafts array provided for processing is empty' }));
        }
        return forkJoin([
          updatedDraftsRequestBody.length > 0 ? prescriptionsService.processPrescriptionsDraftsUpdate(action.memberId, updatedDraftsRequestBody) : of(null),
          newDraftsRequestBody.length > 0 ? prescriptionsService.processPrescriptionDraftsCreate(action.memberId, newDraftsRequestBody) : of(null),
        ]).pipe(
          switchMap(() => {
            storageService.clearDrafts();
            return [processPrescriptionsDraftsSuccess(), clearPrescriptionDrafts(), clearPrescriptions(), refreshPrescriptionsData()];
          }),
          catchError(error => of(processPrescriptionsDraftsFailure({ error: error.message || 'Failed to process drafts' })))
        );
      })
    ),
  { functional: true }
);

export const resetPrescriptionsStateEffect = createEffect(
  (actions$ = inject(Actions), storageService = inject(PrescriptionsDraftsStorageService)): Observable<Action> =>
    actions$.pipe(
      ofType(resetPrescriptionsState),
      tap(() => storageService.clearDrafts())
    ),
  { functional: true, dispatch: false }
);

export const prescriptionsDraftsEffects = [
  loadPrescriptionDraftsFromStorageEffect,
  addPrescriptionDraftEffect,
  updatePrescriptionDraftEffect,
  deletePrescriptionDraftEffect,
  clearPrescriptionDraftsEffect,
  processPrescriptionsDraftsEffect,
  resetPrescriptionsStateEffect
];
